import { Button, TextField } from '@mui/material';
import { useState, useCallback } from 'react';
import { useNotify } from 'react-admin';

import UserAvatar from 'components/UserAvatar/UserAvatar';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { formatAuthorData } from 'utility/functions';

import BaseBubble from '../BaseBubble';
import { useEditorBubbleMenu } from '../../context';

const ThreadsBubble = () => {
  const notify = useNotify();
  const { editor, bubbleViewState, bubbleCleanup } = useEditorBubbleMenu();
  const [comment, setComment] = useState('');
  const { userInfo } = useCosmos();
  const userNameString = userInfo
    ? formatAuthorData(userInfo, { includeEmail: true })
    : 'Unknown User';

  const handleClose = useCallback(() => {
    // Set comment state first
    setComment('');
    // Use a small delay before closing the menu
    bubbleCleanup();
  }, [bubbleCleanup]);

  const createThread = useCallback(() => {
    if (comment === '' || !userInfo || !editor) {
      return;
    }

    const from = editor?.state?.selection?.from;
    const to = editor?.state?.selection?.to;
    const selection = editor?.state?.doc?.textBetween(from, to, ' ');

    const success = editor
      .chain()
      .focus()
      .setThread({
        content: comment,
        commentData: {
          userId: userInfo?.uuid,
          selection: selection,
          from: from,
          to: to,
        },
      })
      .run();

    if (success) {
      notify('Comment added.', { type: 'info' });
      handleClose();
    } else {
      console.error('[createThread] Failed to add comment - logging editor:', editor);
      notify('Failed to add comment.', { type: 'error' });
    }
  }, [comment, userInfo, editor, notify, handleClose]);

  if (!editor || bubbleViewState !== 'threadsAddComment') {
    return null;
  }

  return (
    <BaseBubble
      avatar={
        <UserAvatar
          user={userInfo}
          size={32}
        />
      }
      title="Add a Comment"
      subheader={userNameString}
      actions={
        <>
          <Button
            data-testid="bubble-menu-cancel-btn"
            type="button"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            data-testid="bubble-menu-comment-btn"
            type="button"
            variant="contained"
            autoFocus
            onClick={createThread}
          >
            Comment
          </Button>
        </>
      }
    >
      <TextField
        data-testid="bubble-menu-comment-input"
        autoFocus
        required
        variant="outlined"
        id="compose-comment"
        label=""
        placeholder="Comment"
        multiline
        maxRows={4}
        value={comment}
        onChange={(event) => {
          setComment(event.currentTarget.value);
        }}
        sx={(theme) => ({
          width: '100%',
          backgroundColor: theme.palette.background.paper,
        })}
      />
    </BaseBubble>
  );
};

export default ThreadsBubble;
